import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { createLoginSchema, type LoginFormData } from '../../lib/validators';
import {
  useLocalizedSchema,
  useRevalidateOnLanguageChange,
} from '../../hooks/useLocalizedSchema';
import { useAuth, redirectAfterLogin } from '../../hooks/useAuth';
import { getErrorMessage, extractApiError } from '../../lib/errorCodes';
import { AppSelector } from '../../components/shared/AppSelectorModal';
import { SessionConfirmModal } from '../../components/shared/SessionConfirmModal';
import { TextField, PasswordField, Checkbox, Button } from '../../components/ui/Form';
import type { AppInfo } from '../../types/auth';
import './LoginPage.css';

const REMEMBERED_EMAIL_KEY = 'remembered_email';

export default function LoginPage() {
  const { t } = useTranslation();
  const { login, expireAndRelogin } = useAuth();
  const navigate = useNavigate();

  const rememberedEmail = localStorage.getItem(REMEMBERED_EMAIL_KEY) ?? '';
  const [remember, setRemember] = useState(Boolean(rememberedEmail));

  const [isLoading, setIsLoading] = useState(false);
  const [showAppSelector, setShowAppSelector] = useState(false);
  const [selectedApps, setSelectedApps] = useState<AppInfo[]>([]);
  const [selectedToken, setSelectedToken] = useState('');

  const [showSessionModal, setShowSessionModal] = useState(false);
  const [pendingSessionToken, setPendingSessionToken] = useState('');
  const [isExpiring, setIsExpiring] = useState(false);
  const [pendingCredentials, setPendingCredentials] = useState<LoginFormData | null>(null);
  const [formDisabled, setFormDisabled] = useState(false);

  const loginSchema = useLocalizedSchema(createLoginSchema);

  const {
    register,
    handleSubmit,
    trigger,
    formState: { errors, isSubmitted },
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { username: rememberedEmail, password: '' },
  });

  useRevalidateOnLanguageChange(trigger, isSubmitted);

  const persistEmail = (email: string) => {
    if (remember) {
      localStorage.setItem(REMEMBERED_EMAIL_KEY, email);
    } else {
      localStorage.removeItem(REMEMBERED_EMAIL_KEY);
    }
  };

  const finalizeLogin = (result: { apps?: AppInfo[]; access_token?: string }) => {
    if (result.apps && result.apps.length > 1) {
      setSelectedApps(result.apps);
      setSelectedToken(result.access_token || '');
      setShowAppSelector(true);
      return;
    }

    const redirected = redirectAfterLogin(result.access_token!, result.apps);
    if (!redirected) {
      toast.error(t('apiErrors.noAppsAssigned'));
    }
  };

  const onSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    try {
      const result = await login(data.username, data.password);

      if (result.error) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidCredentials'));
        return;
      }

      if (result.hasExistingSession) {
        setPendingSessionToken(result.sessionToken || '');
        setPendingCredentials(data);
        setFormDisabled(true);
        setShowSessionModal(true);
        return;
      }

      if (result.success && result.access_token) {
        persistEmail(data.username);
        finalizeLogin(result);
      }
    } catch (error) {
      toast.error(extractApiError(error));
    } finally {
      setIsLoading(false);
    }
  };

  const handleExpireAndContinue = async () => {
    if (!pendingCredentials || !pendingSessionToken) return;
    setIsExpiring(true);
    try {
      const result = await expireAndRelogin(
        pendingSessionToken,
        pendingCredentials.username,
        pendingCredentials.password
      );

      if (result.error) {
        toast.error(getErrorMessage(result.errorCode, 'apiErrors.invalidCredentials'));
        setShowSessionModal(false);
        setFormDisabled(false);
        return;
      }

      setShowSessionModal(false);
      setFormDisabled(false);

      if (result.success && result.access_token) {
        persistEmail(pendingCredentials.username);
        finalizeLogin(result);
      }
    } catch (error) {
      toast.error(extractApiError(error));
      setShowSessionModal(false);
      setFormDisabled(false);
    } finally {
      setIsExpiring(false);
      setIsLoading(false);
    }
  };

  const handleCancelSession = () => {
    setShowSessionModal(false);
    setFormDisabled(false);
    setPendingSessionToken('');
    setPendingCredentials(null);
    setIsLoading(false);
  };

  return (
    <>
      <div className="auth-login__header">
        <h1 className="auth-login__title">
          {t('auth.loginTitle')}
          <br />
          {t('auth.loginTitleBrand')}
        </h1>
        <p className="auth-login__subtitle">{t('auth.loginSubtitle')}</p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className={`auth-login__form${formDisabled ? ' auth-login__form--disabled' : ''}`}
        noValidate
      >
        <TextField
          label={t('general.emailLabel')}
          type="email"
          placeholder="votre.email@exemple.com"
          autoComplete="email"
          error={errors.username?.message}
          {...register('username')}
        />

        <PasswordField
          label={t('general.passwordLabel')}
          placeholder="••••••••••••"
          autoComplete="current-password"
          showLabel={t('auth.showPassword')}
          hideLabel={t('auth.hidePassword')}
          error={errors.password?.message}
          {...register('password')}
        />

        <div className="auth-login__options">
          <Checkbox
            label={t('auth.rememberMe')}
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
          />
          <button type="button" className="auth-link" onClick={() => navigate('/forgot-password')}>
            {t('general.forgotPasswordLink')}
          </button>
        </div>

        <div className="auth-login__submit">
          <Button type="submit" isLoading={isLoading} disabled={formDisabled} withArrow>
            {t('general.loginButton')}
          </Button>
        </div>
      </form>

      {showSessionModal && (
        <SessionConfirmModal
          onContinue={handleExpireAndContinue}
          onCancel={handleCancelSession}
          isLoading={isExpiring}
        />
      )}

      {showAppSelector && (
        <AppSelector
          apps={selectedApps}
          token={selectedToken}
          onClose={() => setShowAppSelector(false)}
        />
      )}
    </>
  );
}
